// Copyright ©️ 2026 eVolve MEP, LLC

import React, { type ReactNode } from 'react';

import {
  type AssignedCustomProperty,
  type CustomPropertyId,
  type CustomPropertyDataTypeMap,
  type CustomPropertyTypeName,
  formatDate,
  type DateOnly,
} from '@evolvemep/api-ts';
import { Loader, Box, Text } from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { customPropertyEditor } from 'components/Mantine/CustomProperties/getCustomPropertyColDefs';
import { useCustomProperties } from 'components/Mantine/CustomProperties/useCustomProperties';
import { EditableCell, type EditorComponent } from 'helpers/ag-grid/EditableCells/EditableCell';

export type CustomPropertiesListProps<T extends { customProperties: AssignedCustomProperty[] }> = {
  entity: T;
  onUpdateCustomProperty: (
    customPropertyId: CustomPropertyId,
    newValue: CustomPropertyDataTypeMap[keyof CustomPropertyDataTypeMap] | null,
  ) => Promise<void>;
  customPropertyTypeName: CustomPropertyTypeName;
  // Custom properties are always editable, hence the lack of an `editable` prop
  // Instead, we just disable this component in situations like loading/saving
  disabled: boolean;
  onStartEditingCustomProperty?: () => void;
  onEndEditingCustomProperty?: () => void;
  nothingFoundMessage?: ReactNode;
};

export const CustomPropertiesList = <T extends { customProperties: AssignedCustomProperty[] }>({
  entity,
  customPropertyTypeName,
  onUpdateCustomProperty,
  disabled,
  onStartEditingCustomProperty,
  onEndEditingCustomProperty,
  nothingFoundMessage,
}: CustomPropertiesListProps<T>) => {
  const { customProperties, loading } = useCustomProperties(customPropertyTypeName);

  if (loading) return <Loader type="dots" />;
  if (customProperties?.length === 0) {
    return (
      nothingFoundMessage ?? (
        <Text fz="sm" c="dimmed">
          No custom properties defined.
        </Text>
      )
    );
  }

  return customProperties?.map((property) => {
    const value = entity.customProperties.find((p) => p.customPropertyId === property.customPropertyId)?.value;
    return (
      <EditableCell<CustomPropertyDataTypeMap[typeof property.dataType]>
        key={property.customPropertyId}
        label={property.name}
        labelTooltip={property.description}
        initialValue={value ?? null}
        onSave={async (newValue) => onUpdateCustomProperty(property.customPropertyId, newValue)}
        renderValue={
          property.dataType === 'toggle' ? (
            <Box ml={value ? -4 : 0} h={25}>
              {value ? <EvolveIcon icon="Check" /> : '--'}
            </Box>
          ) : property.dataType === 'date' && !!value ? (
            formatDate(value as DateOnly, 'MM/dd/yyyy')
          ) : property.dataType === 'dropdown' ? (
            (property.options?.find((o) => o.customPropertyOptionId === value)?.name ?? '--')
          ) : (
            (value ?? '--')
          )
        }
        Editor={customPropertyEditor(property) as EditorComponent<CustomPropertyDataTypeMap[typeof property.dataType]>}
        closeOnChange={
          property.dataType === 'date' || property.dataType === 'dropdown' || property.dataType === 'toggle'
        }
        clearable={property.dataType !== 'toggle'}
        editable={!disabled}
        className={`${!disabled ? 'editable' : ''}`}
        confirmationText="Done"
        onOpen={onStartEditingCustomProperty}
        onClose={onEndEditingCustomProperty}
      />
    );
  });
};
